import { z } from 'zod';

import { buildAnchoredPrompt } from './prompt';

export const GPT_IMAGE_2_MODEL = 'gpt-image-2' as const;
export const PANO_SIZE = '2048x1024' as const;

const IMAGES_GENERATIONS_ENDPOINT = 'https://api.openai.com/v1/images/generations';
const DEFAULT_PARALLEL = 3;
const DEFAULT_QUALITY = 'high' as const;

const generationResponseSchema = z.object({
  data: z
    .array(
      z.object({
        b64_json: z.string(),
        revised_prompt: z.string().optional(),
      }),
    )
    .min(1),
});

export type GenQuality = 'high' | 'medium';

export class OpenAIError extends Error {
  status: number;
  bodyText: string;
  retryAfterSeconds?: number;

  constructor(status: number, bodyText: string, retryAfterSeconds?: number) {
    super(`OpenAI image generation failed (${status})`);
    this.name = 'OpenAIError';
    this.status = status;
    this.bodyText = bodyText;
    this.retryAfterSeconds = retryAfterSeconds;
  }
}

export type GenerateKeyframeArgs = {
  location: string;
  year: number;
  apiKey: string;
  quality?: GenQuality;
  extraDirections?: string;
  signal?: AbortSignal;
};

export type Keyframe = { year: number; b64: string; revisedPrompt?: string };

export async function generateKeyframe(a: GenerateKeyframeArgs): Promise<Keyframe> {
  throwIfAborted(a.signal);

  const response = await fetch(IMAGES_GENERATIONS_ENDPOINT, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${a.apiKey}`,
    },
    body: JSON.stringify({
      model: GPT_IMAGE_2_MODEL,
      prompt: buildAnchoredPrompt({
        location: a.location,
        year: a.year,
        extraDirections: a.extraDirections,
      }),
      size: PANO_SIZE,
      quality: a.quality ?? DEFAULT_QUALITY,
      n: 1,
      output_format: 'png',
      background: 'opaque',
      moderation: 'auto',
      response_format: 'b64_json',
    }),
    signal: a.signal,
  });

  const bodyText = await response.text();

  if (!response.ok) {
    throw new OpenAIError(
      response.status,
      bodyText,
      response.status === 429
        ? parseRetryAfterSeconds(response.headers.get('Retry-After'))
        : undefined,
    );
  }

  let parsedBody: unknown;
  try {
    parsedBody = JSON.parse(bodyText);
  } catch (error) {
    throw new OpenAIError(500, `malformed response: ${getErrorMessage(error)}`);
  }

  const parsed = generationResponseSchema.safeParse(parsedBody);
  if (!parsed.success) {
    throw new OpenAIError(500, `malformed response: ${parsed.error.message}`);
  }

  const frame = parsed.data.data[0];
  if (!frame) {
    throw new OpenAIError(500, 'malformed response: missing image payload');
  }

  return {
    year: a.year,
    b64: frame.b64_json,
    revisedPrompt: frame.revised_prompt,
  };
}

export type GenerateJourneyArgs = {
  location: string;
  years: readonly number[];
  apiKey: string;
  quality?: GenQuality;
  extraDirections?: string;
  maxParallel?: number;
  onFrameLoaded?: (kf: Keyframe, idx: number) => void;
  onFrameError?: (err: Error, year: number, idx: number) => void;
  signal?: AbortSignal;
};

export type JourneyResult = {
  keyframes: Keyframe[];
  failures: Array<{ year: number; error: Error }>;
};

export async function generateJourney(a: GenerateJourneyArgs): Promise<JourneyResult> {
  throwIfAborted(a.signal);

  const maxParallel = normalizeParallel(a.maxParallel);
  const orderedKeyframes = new Array<Keyframe | undefined>(a.years.length);
  const failures: Array<{ year: number; error: Error }> = [];

  for (let start = 0; start < a.years.length; start += maxParallel) {
    throwIfAborted(a.signal);

    const chunk = a.years.slice(start, start + maxParallel);
    const settled = await Promise.allSettled(
      chunk.map((year, offset) => {
        const idx = start + offset;
        return generateKeyframe({
          location: a.location,
          year,
          apiKey: a.apiKey,
          quality: a.quality,
          extraDirections: a.extraDirections,
          signal: a.signal,
        })
          .then((kf) => {
            a.onFrameLoaded?.(kf, idx);
            return { idx, keyframe: kf };
          })
          .catch((error: unknown) => {
            if (isAbortError(error)) {
              throw error;
            }

            const normalized = toError(error);
            a.onFrameError?.(normalized, year, idx);
            throw normalized;
          });
      }),
    );

    for (const [offset, result] of settled.entries()) {
      const year = chunk[offset];
      if (year === undefined) {
        continue;
      }

      if (result.status === 'fulfilled') {
        orderedKeyframes[result.value.idx] = result.value.keyframe;
        continue;
      }

      if (isAbortError(result.reason)) {
        throw result.reason;
      }

      failures.push({ year, error: toError(result.reason) });
    }
  }

  throwIfAborted(a.signal);

  return {
    keyframes: orderedKeyframes.filter((keyframe): keyframe is Keyframe => Boolean(keyframe)),
    failures,
  };
}

function normalizeParallel(value?: number): number {
  if (!value || value < 1) {
    return DEFAULT_PARALLEL;
  }
  return Math.floor(value);
}

function throwIfAborted(signal?: AbortSignal): void {
  if (!signal?.aborted) {
    return;
  }

  if (signal.reason instanceof DOMException && signal.reason.name === 'AbortError') {
    throw signal.reason;
  }

  throw new DOMException('The operation was aborted.', 'AbortError');
}

function isAbortError(error: unknown): error is DOMException {
  return error instanceof DOMException && error.name === 'AbortError';
}

function toError(error: unknown): Error {
  if (error instanceof Error) {
    return error;
  }
  return new Error(getErrorMessage(error));
}

function getErrorMessage(error: unknown): string {
  if (error instanceof Error) {
    return error.message;
  }
  return String(error);
}

function parseRetryAfterSeconds(value: string | null): number | undefined {
  if (!value) {
    return undefined;
  }

  const numeric = Number(value);
  if (Number.isFinite(numeric) && numeric >= 0) {
    return Math.ceil(numeric);
  }

  const retryAt = Date.parse(value);
  if (Number.isNaN(retryAt)) {
    return undefined;
  }

  const diffMs = retryAt - Date.now();
  if (diffMs < 0) {
    return 0;
  }

  return Math.ceil(diffMs / 1000);
}
